import { EVENT_TYPES, TypeIdSchema, event } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { createInsertSchema, createSelectSchema } from 'drizzle-typebox';
import { CalendarEventSchema } from '../../calendar-api/calendar-event/calendar-event.schema';
import { CalendarSchema } from '../../calendar-api/calendar/calendar.schema';
import { ProviderSchema } from '../../common/provider.schema';
import { EmailDraftSchema } from '../../emails-api/draft/draft.schema';
import { EmailSchema } from '../../emails-api/email/email.schema';
import { EmailFolderSchema } from '../../emails-api/folder/folder.schema';
import { chatSchema } from '../../messaging-api/chat/chat.schema';
import { messageSchema } from '../../messaging-api/message/message.schema';
import { reactionSchema } from '../../shared/reaction/reaction.schema';
import { blockedUserSchema } from '../../users-api/user/blocked-user.schema';
import { userFollowerSchema } from '../../users-api/user/user-follower.schema';
import { userRelationSchema } from '../../users-api/user/user-relation.schema';
import { StringEnum } from '../../utils/typebox';
import { PublicAccountSchema } from '../account/account.schema';
import { ClickTrackingPayloadSchema, OpenTrackingPayloadSchema } from '../tracking/tracking.schema';

export const EventSchema = createSelectSchema(event, { payload: () => Type.Record(Type.String(), Type.Any()) });

export const InsertEventSchema = createInsertSchema(event);

export const EventTypeSchema = StringEnum([...EVENT_TYPES]);

export const PublicEventSchema = Type.Composite([
  Type.Omit(EventSchema, ['id', 'account_id', 'public_id', 'account_public_id']),
  Type.Object({
    id: TypeIdSchema('event'),
    account_id: TypeIdSchema('account'),
    account_scope_id: Type.Union([TypeIdSchema('accountScope'), Type.Null()], {
      description: 'ID of the Scope assigned to the Account when the Event was created, or `null` for an unscoped Account.',
    }),
    object: Type.Literal('Event'),
    account_provider: Type.Optional(ProviderSchema),
    account_name: Type.Optional(Type.String()),
    application_id: TypeIdSchema('app'),
    application_production: Type.Boolean(),
  }),
]);

export type Event = typeof event.$inferSelect;
export type InsertEvent = typeof event.$inferInsert;
export type PublicEvent = Static<typeof PublicEventSchema>;

/**
 * @warning
 * payload SHOULD NEVER be null
 */

const AccountStatusDisconnectedEventSchema = Type.Object({
  type: Type.Literal('account.status.disconnected'),
  payload: Type.Object({
    timestamp: Type.String(),
    product: Type.Optional(Type.String()),
  }),
});

const AccountStatusRunningEventSchema = Type.Object({
  type: Type.Literal('account.status.running'),
  payload: Type.Object({
    timestamp: Type.String(),
    product: Type.Optional(Type.String()),
  }),
});

const AccountStatusErroredEventSchema = Type.Object({
  type: Type.Literal('account.status.errored'),
  payload: Type.Object({
    timestamp: Type.String(),
    product: Type.Optional(Type.String()),
    reason: StringEnum(['proxy', 'provider', 'unipile', 'action_required']),
  }),
});

const AccountStatusDegradedEventSchema = Type.Object({
  type: Type.Literal('account.status.degraded'),
  payload: Type.Object({
    timestamp: Type.String(),
    product: Type.Optional(Type.String()),
    reason: Type.Optional(StringEnum(['proxy', 'provider', 'unipile', 'action_required'])),
  }),
});

const AccountStatusPartialEventSchema = Type.Object({
  type: Type.Literal('account.status.partial'),
  payload: Type.Object({
    timestamp: Type.String(),
    product: Type.Optional(Type.String()),
  }),
});

const AccountLockedEventSchema = Type.Object({
  type: Type.Literal('account.locked'),
  payload: Type.Object({
    timestamp: Type.String(),
  }),
});

const AccountUnlockedEventSchema = Type.Object({
  type: Type.Literal('account.unlocked'),
  payload: Type.Object({
    timestamp: Type.String(),
  }),
});

const AccountInitialSyncStatusRunningEventSchema = Type.Object({
  type: Type.Literal('account.initial_sync.running'),
  payload: Type.Object({
    timestamp: Type.String(),
  }),
});

const AccountInitialSyncStatusFailedEventSchema = Type.Object({
  type: Type.Literal('account.initial_sync.failed'),
  payload: Type.Object({
    timestamp: Type.String(),
  }),
});

const AccountInitialSyncStatusCompletedEventSchema = Type.Object({
  type: Type.Literal('account.initial_sync.completed'),
  payload: Type.Object({
    timestamp: Type.String(),
  }),
});

const AccountAddEventSchema = Type.Object({
  type: Type.Literal('account.add'),
  payload: Type.Object({
    account: PublicAccountSchema,
    state: Type.Optional(Type.String()),
  }),
});

const AccountReconnectEventSchema = Type.Object({
  type: Type.Literal('account.reconnect'),
  payload: Type.Object({
    account: PublicAccountSchema,
    state: Type.Optional(Type.String()),
  }),
});

const AccountRemoveEventSchema = Type.Object({
  type: Type.Literal('account.remove'),
  payload: Type.Object({
    account: Type.Object({
      id: TypeIdSchema('account'),
    }),
  }),
});

const MessageNewEventSchema = Type.Object({
  type: Type.Literal('message.new'),
  payload: messageSchema,
});

const MessageUpdateEventSchema = Type.Object({
  type: Type.Literal('message.update'),
  payload: messageSchema,
});

const MessageDeleteEventSchema = Type.Object({
  type: Type.Literal('message.delete'),
  payload: Type.Object({ chat_id: Type.String(), id: Type.String() }),
});

const MessageReadEventSchema = Type.Object({
  type: Type.Literal('message.receipt.read'),
  payload: Type.Object({ chat_id: Type.String(), message_id: Type.String(), user_id: Type.String(), timestamp: Type.String() }),
});

const MessageDeliveryEventSchema = Type.Object({
  type: Type.Literal('message.receipt.delivery'),
  payload: Type.Object({ chat_id: Type.String(), message_id: Type.String(), user_id: Type.String(), timestamp: Type.String() }),
});

const MessageReactionNewEventSchema = Type.Object({
  type: Type.Literal('message.reaction.new'),
  payload: Type.Object({ chat_id: Type.String(), message_id: Type.String(), reaction: reactionSchema }),
});

const MessageReactionDeleteEventSchema = Type.Object({
  type: Type.Literal('message.reaction.delete'),
  payload: Type.Object({
    chat_id: Type.String(),
    message_id: Type.String(),
    sender_id: Type.String(),
    value: Type.Optional(Type.String({ description: 'Value of the removed reaction. Usually an emoji unicode.' })),
  }),
});

const ChatDeleteEventSchema = Type.Object({
  type: Type.Literal('chat.delete'),
  payload: Type.Object({ id: Type.String() }),
});

const ChatUpdateEventSchema = Type.Object({
  type: Type.Literal('chat.update'),
  payload: chatSchema,
});

const EmailNewEventSchema = Type.Object({
  type: Type.Literal('email.new'),
  payload: Type.Object({
    email: EmailSchema,
    folder_id: Type.String(),
  }),
});

const EmailBonceDetectionEventSchema = Type.Object({
  type: Type.Literal('email.new.bounce'),
  payload: Type.Object({
    reason: Type.String(),
    date: Type.String(),
    addresses: Type.String(),
    error_persistence: StringEnum(['temporary', 'permanent', 'unknown']),
    type: Type.String(),
    code: Type.Number(),
    email_id: Type.String(),
  }),
});

/**
 * An email was modified in place. The modification itself is read off the email the event carries, like every other
 * update event. What a provider lets us detect differs from one provider to the next: today, the categories assigned
 * to the email on Outlook.
 */
const EmailUpdateEventSchema = Type.Object({
  type: Type.Literal('email.update'),
  payload: EmailSchema,
});

const EmailDeleteEventSchema = Type.Object({
  type: Type.Literal('email.delete'),
  payload: Type.Object({
    email: Type.Object({ id: Type.String() }),
    folder_id: Type.String(),
  }),
});

const EmailDraftNewEventSchema = Type.Object({
  type: Type.Literal('email.draft.new'),
  payload: EmailDraftSchema,
});

const EmailDraftDeleteEventSchema = Type.Object({
  type: Type.Literal('email.draft.delete'),
  payload: Type.Object({ id: Type.String() }),
});

const EmailFolderCreateEventSchema = Type.Object({
  type: Type.Literal('email.folder.create'),
  payload: EmailFolderSchema,
});

const EmailFolderUpdateEventSchema = Type.Object({
  type: Type.Literal('email.folder.update'),
  payload: EmailFolderSchema,
});

const EmailFolderDeleteEventSchema = Type.Object({
  type: Type.Literal('email.folder.delete'),
  payload: Type.Object({ id: Type.String() }),
});

const CalendarCreateEventSchema = Type.Object({
  type: Type.Literal('calendar.create'),
  payload: CalendarSchema,
});

const CalendarUpdateEventSchema = Type.Object({
  type: Type.Literal('calendar.update'),
  payload: CalendarSchema,
});

const CalendarDeleteEventSchema = Type.Object({
  type: Type.Literal('calendar.delete'),
  payload: Type.Object({ id: Type.String() }),
});

const CalendarEventNewEventSchema = Type.Object({
  type: Type.Literal('calendar.event.new'),
  payload: CalendarEventSchema,
});

const CalendarEventUpdateEventSchema = Type.Object({
  type: Type.Literal('calendar.event.update'),
  payload: CalendarEventSchema,
});

const CalendarEventDeleteEventSchema = Type.Object({
  type: Type.Literal('calendar.event.delete'),
  payload: Type.Object({ calendar_id: Type.String(), id: Type.String() }),
});

const TrackingOpenEventSchema = Type.Object({
  type: Type.Literal('tracking.open'),
  payload: OpenTrackingPayloadSchema,
});

const TrackingClickEventSchema = Type.Object({
  type: Type.Literal('tracking.click'),
  payload: ClickTrackingPayloadSchema,
});

const RelationNewEventSchema = Type.Object({
  type: Type.Literal('relation.new'),
  payload: userRelationSchema,
});

const FollowerNewEventSchema = Type.Object({
  type: Type.Literal('follower.new'),
  payload: userFollowerSchema,
});

const BlockedNewEventSchema = Type.Object({
  type: Type.Literal('blocked.new'),
  payload: blockedUserSchema,
});

export const AccountRealtimeEventSchema = Type.Union([
  AccountLockedEventSchema,
  AccountUnlockedEventSchema,
  AccountStatusDisconnectedEventSchema,
  AccountStatusRunningEventSchema,
  AccountStatusErroredEventSchema,
  AccountStatusDegradedEventSchema,
  AccountStatusPartialEventSchema,
  AccountAddEventSchema,
  AccountReconnectEventSchema,
  AccountRemoveEventSchema,
  AccountInitialSyncStatusRunningEventSchema,
  AccountInitialSyncStatusCompletedEventSchema,
  AccountInitialSyncStatusFailedEventSchema,
]);

export const RealtimeProviderEventSchema = Type.Union([
  MessageNewEventSchema,
  MessageUpdateEventSchema,
  MessageDeleteEventSchema,
  MessageReadEventSchema,
  MessageDeliveryEventSchema,
  MessageReactionNewEventSchema,
  MessageReactionDeleteEventSchema,
  ChatDeleteEventSchema,
  ChatUpdateEventSchema,
  EmailNewEventSchema,
  EmailBonceDetectionEventSchema,
  EmailUpdateEventSchema,
  EmailDeleteEventSchema,
  EmailDraftNewEventSchema,
  EmailDraftDeleteEventSchema,
  CalendarCreateEventSchema,
  CalendarUpdateEventSchema,
  CalendarDeleteEventSchema,
  CalendarEventNewEventSchema,
  CalendarEventUpdateEventSchema,
  CalendarEventDeleteEventSchema,
  EmailFolderCreateEventSchema,
  EmailFolderUpdateEventSchema,
  EmailFolderDeleteEventSchema,
  TrackingOpenEventSchema,
  TrackingClickEventSchema,
  RelationNewEventSchema,
  FollowerNewEventSchema,
  BlockedNewEventSchema,
]);

export const RealtimeEventSchema = Type.Union([AccountRealtimeEventSchema, RealtimeProviderEventSchema]);

export type RealtimeEvent = Static<typeof RealtimeEventSchema>;
export type AccountRealtimeEvent = Static<typeof AccountRealtimeEventSchema>;
export type AccountInitialSyncRealtimeEvent = Extract<AccountRealtimeEvent, { type: `account.initial_sync.${string}` }>;
export type RealtimeProviderEvent = Static<typeof RealtimeProviderEventSchema>;

/**
 * Identical events are stored once and delivered once. When a provider can legitimately report the same event
 * again (e.g. an email moved back to a folder it already left), it sets `deduplication_key` to something that
 * identifies this occurrence. The key only takes part in deduplication and is never delivered.
 */
export type DispatchedRealtimeEvent = RealtimeEvent & { deduplication_key?: string };
export type DispatchedRealtimeProviderEvent = RealtimeProviderEvent & { deduplication_key?: string };

export type EventType = RealtimeEvent['type'];
